import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, X } from "lucide-react";
import MarkdownText from "../common/MarkdownText";
import DropPointsButton from "../events/DropPointsButton";
import EventVenueName from "../events/EventVenueName";
import JoinWhatsappGroupLink from "../events/JoinWhatsappGroupLink";
import RequestPickupButton from "../events/RequestPickupButton";
import {
  canRegisterForEvent,
  eventCardMedia,
  eventPickupEnabled,
  eventRegistrationComingSoon,
  eventRegistrationNotRequired,
  NO_REGISTRATION_NEEDED_LABEL,
  publicDropPoints,
  publicWhatsappGroupUrl,
  REGISTRATIONS_OPENING_SOON_LABEL,
} from "../../utils/publicEventsApi";

function copyText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function clampScale(scale) {
  return Math.min(2, Math.max(1, scale));
}

function FlyerStage({ src, alt }) {
  const stageRef = useRef(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0, originX: 50, originY: 50 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const suppressClick = useRef(false);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const block = (event) => {
      if (event.touches.length > 1 || viewRef.current.scale > 1) event.preventDefault();
    };
    el.addEventListener("touchmove", block, { passive: false });
    return () => el.removeEventListener("touchmove", block);
  }, []);

  const onPointerDown = (event) => {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = {
        type: "pinch",
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        scale: viewRef.current.scale,
      };
      return;
    }
    if (viewRef.current.scale > 1) {
      gesture.current = {
        type: "pan",
        x: event.clientX,
        y: event.clientY,
        ox: viewRef.current.x,
        oy: viewRef.current.y,
      };
    }
  };

  const onPointerMove = (event) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const current = gesture.current;
    if (!current) return;
    if (current.type === "pinch" && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const scale = clampScale(current.scale * (dist / current.dist));
      suppressClick.current = true;
      setView((prev) => ({
        ...prev,
        scale,
        x: scale === 1 ? 0 : prev.x,
        y: scale === 1 ? 0 : prev.y,
      }));
      return;
    }
    if (current.type === "pan") {
      const dx = event.clientX - current.x;
      const dy = event.clientY - current.y;
      if (Math.hypot(dx, dy) > 6) suppressClick.current = true;
      setView((prev) => ({ ...prev, x: current.ox + dx, y: current.oy + dy }));
    }
  };

  const onPointerUp = (event) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) gesture.current = null;
  };

  const onClick = (event) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setView((prev) => {
      if (prev.scale > 1) return { scale: 1, x: 0, y: 0, originX: 50, originY: 50 };
      const originX = rect.width
        ? ((event.clientX - rect.left) / rect.width) * 100
        : 50;
      const originY = rect.height
        ? ((event.clientY - rect.top) / rect.height) * 100
        : 50;
      return { scale: 2, x: 0, y: 0, originX, originY };
    });
  };

  return (
    <div
      ref={stageRef}
      className="overflow-hidden"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={onClick}
      style={{ touchAction: view.scale > 1 ? "none" : "pan-y" }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        data-zoom={view.scale}
        className="mx-auto block h-auto max-h-[calc(100vh-8rem)] w-auto max-w-full select-none object-contain"
        style={{
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          transformOrigin: `${view.originX}% ${view.originY}%`,
        }}
      />
    </div>
  );
}

const EventFlyerModal = ({ event, onClose, onRegister }) => {
  const flyerSrc = eventCardMedia(event).flyer;
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!flyerSrc) return undefined;
    const previous = document.activeElement;
    const root = dialogRef.current;
    closeRef.current?.focus();
    const onKey = (keyEvent) => {
      if (document.querySelector(".smym-sheet, .smym-drawer")) return;
      if (keyEvent.key === "Escape") {
        if (keyEvent.defaultPrevented) return;
        keyEvent.preventDefault();
        onClose();
        return;
      }
      if (keyEvent.key !== "Tab" || !root) return;
      const items = [
        ...root.querySelectorAll(
          'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])',
        ),
      ];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (keyEvent.shiftKey && (active === first || !root.contains(active))) {
        keyEvent.preventDefault();
        last.focus();
      } else if (!keyEvent.shiftKey && active === last) {
        keyEvent.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, [flyerSrc, onClose]);

  if (!flyerSrc) return null;

  const description = copyText(event.description);
  const specialMention = copyText(event.specialMention);
  const venue = copyText(event.venue);
  const notRequired = eventRegistrationNotRequired(event);
  const comingSoon = eventRegistrationComingSoon(event);
  const showRegister = canRegisterForEvent(event) && Boolean(onRegister);
  const closedLabel = comingSoon
    ? REGISTRATIONS_OPENING_SOON_LABEL
    : !notRequired && event.registrationOpen === false
      ? /closed|sold out|full/i.test(event.status || "")
        ? event.status
        : "Registrations closed"
      : "";
  const groupUrl = publicWhatsappGroupUrl(event);
  const showDrop = publicDropPoints(event).length > 0;
  const showPickup = eventPickupEnabled(event);
  const hasCopy = Boolean(
    description ||
    specialMention ||
    venue ||
    notRequired ||
    groupUrl ||
    showDrop ||
    showPickup ||
    showRegister,
  );

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 overflow-y-auto bg-black/90"
      role="dialog"
      aria-modal="true"
      aria-label={event.name ? `${event.name} details` : "Event flyer"}
      onClick={onClose}
    >
      <button
        ref={closeRef}
        type="button"
        onClick={(clickEvent) => {
          clickEvent.stopPropagation();
          onClose();
        }}
        aria-label="Close"
        className="fixed right-[max(0.75rem,env(safe-area-inset-right))] top-[max(0.75rem,env(safe-area-inset-top))] z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white text-[var(--smym-ink)] shadow-lg"
      >
        <X size={28} aria-hidden />
      </button>

      <div
        className="mx-auto w-full max-w-3xl px-4 pb-8 pt-16"
        onClick={(clickEvent) => clickEvent.stopPropagation()}
      >
        {closedLabel ? (
          <p
            role="status"
            className="bg-[var(--smym-ink)] px-4 py-2 text-center text-sm font-semibold uppercase tracking-wide text-[var(--smym-paper)]"
          >
            {closedLabel}
          </p>
        ) : null}
        <FlyerStage src={flyerSrc} alt={event.name || "Event flyer"} />
        {hasCopy ? (
          <div className="mt-4 space-y-3 rounded-xl bg-[var(--smym-paper)] p-5 text-sm leading-relaxed text-[var(--smym-ink-muted)]">
            {notRequired ? (
              <p className="font-semibold text-[var(--smym-ink)]">
                {NO_REGISTRATION_NEEDED_LABEL}
              </p>
            ) : null}
            {venue ? (
              <p className="font-medium text-[var(--smym-ink)]">
                <EventVenueName event={event} />
              </p>
            ) : null}
            {description ? <MarkdownText>{description}</MarkdownText> : null}
            {specialMention ? (
              <div className="smym-event-sponsors">
                <span className="smym-event-sponsors__label">Special mention</span>{" "}
                <MarkdownText inline>{specialMention}</MarkdownText>
              </div>
            ) : null}
            {showRegister || showDrop || showPickup || groupUrl ? (
              <div className="flex flex-col gap-2">
                {showRegister ? (
                  <button
                    type="button"
                    className="smym-btn smym-btn-primary min-h-11 w-full"
                    onClick={() => {
                      onRegister(event);
                      onClose();
                    }}
                  >
                    Register
                    <ChevronRight className="h-5 w-5 shrink-0" aria-hidden />
                  </button>
                ) : null}
                {showDrop || showPickup ? (
                  <div className="flex flex-wrap gap-2">
                    <DropPointsButton
                      event={event}
                      className="smym-btn smym-btn-primary min-h-11 min-w-[min(100%,10rem)] grow shrink basis-[calc(50%-0.25rem)]"
                    />
                    <RequestPickupButton
                      event={event}
                      className="smym-btn smym-btn-primary min-h-11 min-w-[min(100%,10rem)] grow shrink basis-[calc(50%-0.25rem)]"
                    />
                  </div>
                ) : null}
                {groupUrl ? (
                  <JoinWhatsappGroupLink
                    event={event}
                    className="smym-btn smym-btn-primary min-h-11 w-full"
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EventFlyerModal;
